/* tslint:disable:max-file-line-count */
import { action, computed, observable } from 'mobx';

import { AnalyticsStore, ContextStore, SerializedData, Store } from 'bernie-core';
import { getBexApiContext } from 'bernie-graphql';
import { ILocalization } from 'bernie-l10n';
import { Logger, NOOP_LOGGER } from 'bernie-logger';

import { BexApiSource } from 'sources/bex-api-source';

import {
  buildMultiItemContext,
  CartInfo,
  CollectionBusinessModel,
  INCOMPATIBLE_RATEPLAN,
  loadCartFromStorage,
  MultiItemStoreStatus,
  saveCartInStorage,
  updateStatus
} from './multi-item-store-util';

import { InCartProperty, MaxRoomsFailureReason, MultiItemContext, PrepareCheckoutResponse } from 'models/multi-item/multi-item-types';

import { UitkBannerProps } from 'uitk-react-banner';

import {
  AddPropertyToCart,
  CartPropertySearchContextInput,
  CartTtl,
  EmptyCart,
  MoneyInput,
  PrepareCheckoutByCartId,
  PropertyNaturalKeyInput,
  RemoveItemFromCart,
  Uuid
} from '__generated__/api';
import { cartItemToInCartProperty } from 'components/multi-item/cart-adapter';

export class MultiItemStore extends Store {
  constructor(private bexApiSource: BexApiSource, state: SerializedData = {}, logger: Logger = NOOP_LOGGER, private l10n: ILocalization) {
    super(state, logger);
    if (!this.isServerSide()) {
      this.cartInfo = loadCartFromStorage();
    }
    this.setError = this.setError.bind(this);
    this.isAllSoldOut = this.isAllSoldOut.bind(this);
    this.calculateTotalPrice = this.calculateTotalPrice.bind(this);
  }

  public hydrate(data: SerializedData): void {
    if (data) {
      delete data.bexApiSource;
      delete data.l10n;
      Object.assign(this, data);
    }
  }

  @observable
  public itemCount = 0;
  @observable
  public items: InCartProperty[] = [];
  @observable
  public status: MultiItemStoreStatus;
  @observable
  public error: UitkBannerProps;
  @observable
  public cartInfo: CartInfo;
  public multiItemContext: MultiItemContext;
  public referrer: string;
  public isMixMatchEnabled: boolean = false;

  @action
  public async getCartDetails(context: ContextStore): Promise<InCartProperty[]> {
    if (!this.cartInfo || !this.cartInfo.id) {
      return Promise.reject('No Cart has been initialized yet');
    }
    this.status = this.updateStatusWrapper(MultiItemStoreStatus.FETCHING);

    return this.bexApiSource
      .fetchCart({
        context: getBexApiContext(context),
        id: this.cartInfo.id
      })
      .then(({ data: value }) => {
        if (!value || !value.cart || !value.cart.items) {
          return Promise.reject('no items returned');
        }
        this.itemCount = value.cart.itemCount;

        this.items = value.cart.items.map(cartItemToInCartProperty(this.isMixMatchEnabled));
        this.status = this.updateStatusWrapper(MultiItemStoreStatus.FETCH_SUCCESS);

        return Promise.resolve(this.items);
      })
      .catch(error => {
        if (this.logger && typeof this.logger.logEvent === 'function') {
          this.status = this.updateStatusWrapper(MultiItemStoreStatus.FETCH_FAILURE, error);
        }
        if (error.toString().includes('CHECK_IN_DATE_IN_PAST_ERROR')) {
          this.resetCart();
        }

        return Promise.reject(error);
      });
  }

  @action
  public addPropertyToCart(
    context: ContextStore,
    propertyNaturalKeyInputs: PropertyNaturalKeyInput[],
    cartPropertySearchContextInput: CartPropertySearchContextInput,
    businessModel: CollectionBusinessModel,
    analytics?: AnalyticsStore,
    isNoCC?: boolean
  ): Promise<AddPropertyToCart.Mutation> {
    this.status = this.updateStatusWrapper(MultiItemStoreStatus.ADDING_PROPERTY);

    return this.getOrCreateCart(context, businessModel, analytics, isNoCC).then(cartInfo => {
      if (cartInfo && cartInfo.type !== CollectionBusinessModel.NA && cartInfo.type !== businessModel) {
        return Promise.reject(INCOMPATIBLE_RATEPLAN);
      }

      if (this.isMixMatchEnabled) {
        this.cartInfo.type = businessModel;
      }

      return this.bexApiSource
        .addPropertyToCart({
          id: cartInfo.id,
          context: getBexApiContext(context),
          propertyOffer: {
            offerIndex: this.itemCount,
            naturalKeys: propertyNaturalKeyInputs
          },
          cartPropertySearchContextInput
        })
        .then(({ data: response }) => {
          if (!response || !response.cartItems || !response.cartItems.addProperty) {
            Promise.reject('add property - failed to parse response');
          }
          this.status = this.updateStatusWrapper(MultiItemStoreStatus.ADDING_PROPERTY_SUCCESS);
          this.itemCount = response.cartItems.addProperty.itemCount;
          saveCartInStorage(this.cartInfo);

          return response;
        })
        .catch(error => {
          this.status = this.updateStatusWrapper(MultiItemStoreStatus.ADDING_PROPERTY_FAILURE, error);

          return Promise.reject(error);
        });
    });
  }

  @action
  public removeItemFromCart(context: ContextStore, itemId: string): Promise<RemoveItemFromCart.Mutation> {
    if (!this.cartInfo || !this.cartInfo.id) {
      this.status = this.updateStatusWrapper(MultiItemStoreStatus.REMOVE_PROPERTY_FAILURE, 'No Cart has been initialized yet');

      return Promise.reject('No Cart has been initialized yet');
    }
    const deletedIndex: number = this.items.findIndex(obj => obj.id === itemId);
    const deletedItem: InCartProperty = this.items[deletedIndex];
    this.items = [...this.items.slice(0, deletedIndex), ...this.items.slice(deletedIndex + 1)];
    this.itemCount--;

    return this.bexApiSource
      .removeItemFromCart({
        cartId: this.cartInfo.id,
        context: getBexApiContext(context),
        itemId
      })
      .then(({ data: response }) => {
        this.status = this.updateStatusWrapper(MultiItemStoreStatus.REMOVE_PROPERTY_SUCCESS);
        this.itemCount = response.cartItems.remove.itemCount;

        return Promise.resolve(response);
      })
      .catch(error => {
        this.items.splice(deletedIndex, 0, deletedItem);
        this.status = this.updateStatusWrapper(MultiItemStoreStatus.REMOVE_PROPERTY_FAILURE, error);
        this.itemCount++;

        return Promise.reject(error);
      });
  }

  @action
  public emptyCart(context: ContextStore): Promise<EmptyCart.Mutation> {
    if (!this.cartInfo || !this.cartInfo.id) {
      this.status = this.updateStatusWrapper(MultiItemStoreStatus.EMPTY_CART_FAILURE, 'No Cart has been initialized yet');

      return Promise.reject('No Cart has been initialized yet');
    }

    return this.bexApiSource
      .emptyCart({
        id: this.cartInfo.id,
        context: getBexApiContext(context)
      })
      .then(({ data: response }) => {
        this.status = this.updateStatusWrapper(MultiItemStoreStatus.EMPTY_CART_SUCCESS);
        this.itemCount = response.cartItems.emptyCart.itemCount;
        this.cartInfo.type = this.getDefaultBusinessModel();
        saveCartInStorage(this.cartInfo);

        return Promise.resolve(response);
      })
      .catch(error => {
        this.status = this.updateStatusWrapper(MultiItemStoreStatus.EMPTY_CART_FAILURE, error);

        return Promise.reject(error);
      });
  }

  @action
  public async prepareCheckout(context: ContextStore, properties: [PropertyNaturalKeyInput]): Promise<PrepareCheckoutResponse> {
    return this.bexApiSource
      .prepareCheckout({
        context: getBexApiContext(context),
        cookies: '',
        properties
      })
      .then(({ data: value }) => {
        if (!value || !value.prepareCheckout || !value.prepareCheckout.checkoutUrl) {
          return Promise.reject('bad response from prepare checkout');
        }
        this.status = this.updateStatusWrapper(MultiItemStoreStatus.PREPARE_CHECKOUT_SUCCESS);

        return Promise.resolve({
          checkoutUrl: value.prepareCheckout.checkoutUrl
        });
      })
      .catch(error => Promise.reject(error));
  }

  @action
  public async prepareCheckoutByCartId(context: ContextStore): Promise<PrepareCheckoutResponse> {
    if (!this.cartInfo || !this.cartInfo.id) {
      return Promise.reject('No Cart has been initialized yet');
    }
    this.status = this.updateStatusWrapper(MultiItemStoreStatus.PREPARE_CHECKOUT_PROCESSING);
    const marketingChannelId = this.items[0] && this.items[0].marketingChannelId;
    const prepareCheckoutVariables: PrepareCheckoutByCartId.Variables = {
      context: getBexApiContext(context),
      id: this.cartInfo.id,
      totalPrice: this.calculateTotalPrice()
    };
    if (marketingChannelId) {
      prepareCheckoutVariables.marketingChannelId = marketingChannelId;
    }

    return this.bexApiSource
      .prepareCheckoutByCartId(prepareCheckoutVariables)
      .then(({ data: value }) => {
        if (value && value.prepareCheckoutByCartId && value.prepareCheckoutByCartId.failureReason) {
          this.status = this.updateStatusWrapper(MultiItemStoreStatus.PREPARE_CHECKOUT_FAILURE, 'max rooms', {
            message: value.prepareCheckoutByCartId.failureReason.message,
            groupLink: value.prepareCheckoutByCartId.failureReason.groupLinkMessage
          });

          return;
        } else {
          if (!value || !value.prepareCheckoutByCartId || !value.prepareCheckoutByCartId.checkoutUrl) {
            return Promise.reject('bad response from prepare checkout');
          }
          this.status = this.updateStatusWrapper(MultiItemStoreStatus.PREPARE_CHECKOUT_SUCCESS);

          return Promise.resolve({
            checkoutUrl: value.prepareCheckoutByCartId.checkoutUrl
          });
        }
      })
      .catch(error => {
        this.status = this.updateStatusWrapper(MultiItemStoreStatus.PREPARE_CHECKOUT_FAILURE, error);

        return Promise.reject(error);
      });
  }

  @action
  public removeUnavailableAndPrepareCheckout(context: ContextStore): Promise<PrepareCheckoutResponse> {
    return this.removeAllUnavailable(context).then(() => this.prepareCheckoutByCartId(context));
  }

  public removeAllUnavailable(context: ContextStore): Promise<RemoveItemFromCart.Mutation[]> {
    if (!this.items) {
      return Promise.reject('There are no items in the cart');
    }

    return Promise.all(this.items.filter(item => !item.ratePlan.available).map(item => this.removeItemFromCart(context, item.id)));
  }

  public isAllSoldOut(): boolean {
    return this.items && !this.items.some(item => item.ratePlan && item.ratePlan.available);
  }

  public cartContainsUnavailItems(): boolean {
    return this.items && this.items.some(item => !item.ratePlan || !item.ratePlan.available);
  }

  public resetStatus() {
    this.status = this.updateStatusWrapper(MultiItemStoreStatus.IDLE);
  }

  public getOrCreateCart(
    context: ContextStore,
    cartType: CollectionBusinessModel,
    analytics?: AnalyticsStore,
    isNoCC?: boolean
  ): Promise<CartInfo> {
    if (this.isServerSide()) {
      return;
    }

    if (!this.cartInfo || !this.cartInfo.id) {
      this.cartInfo = loadCartFromStorage();
    }

    if (this.cartInfo === null) {
      if (isNoCC && this.itemCount === 0) {
        return Promise.reject(INCOMPATIBLE_RATEPLAN);
      }

      return this.createSessionCart(context, analytics).then(cartId => {
        let identifier = 'multiProp';
        if (this.isMixMatchEnabled) {
          identifier = 'mixAndMatch';
        }
        this.cartInfo = {
          id: cartId,
          timestamp: new Date().valueOf(),
          type: cartType ? cartType : this.getDefaultBusinessModel(),
          identifier
        };
        saveCartInStorage(this.cartInfo);

        return this.cartInfo;
      });
    }

    return Promise.resolve(this.cartInfo);
  }

  public isPackageShoppingPath(): boolean {
    return Boolean(this.multiItemContext && this.multiItemContext.id && this.multiItemContext.packageType);
  }

  public updateSessionId(id: string) {
    if (id && this.isPackageShoppingPath()) {
      this.multiItemContext.id = id;
    }
  }

  private createSessionCart(context: ContextStore, analytics?: AnalyticsStore): Promise<Uuid> {
    return this.bexApiSource
      .createSessionCart({
        context: getBexApiContext(context),
        cartOptions: { ttl: CartTtl.TWELVE_HOURS }
      })
      .then(({ data: cartMutation }) => {
        if (analytics) {
          analytics.trackEvent('MultiProp.Create', 'MultiProp Cart Created', analytics.omnitureData, null, true);
        }

        return cartMutation.createSessionCart && cartMutation.createSessionCart.id;
      });
  }

  private setError(error) {
    this.error = error;
  }

  private isServerSide(): boolean {
    return typeof window === 'undefined';
  }

  private updateStatusWrapper(status: MultiItemStoreStatus, error?: string, userError?: MaxRoomsFailureReason): MultiItemStoreStatus {
    return updateStatus(this.logger, this.setError, status, this.l10n, error ? error.toString() : '', userError);
  }

  public buildContext(query: any = {}): void {
    this.multiItemContext = buildMultiItemContext(query);
  }

  @computed
  get isFreshCart() {
    return this.cartInfo && (!this.items || this.items.length === 0);
  }

  @computed
  get isTouchedCart() {
    return this.cartInfo && this.items && this.items.length > 0;
  }

  public isDisambigSearch(): boolean {
    return typeof this.referrer !== 'undefined' && this.referrer.includes('Disambig');
  }

  @computed
  get showMultiPropExperience() {
    return this.cartInfo && this.cartInfo.id;
  }

  @computed
  get showMixMatchExperience() {
    return this.cartInfo && this.cartInfo.id && this.isMixMatchEnabled;
  }

  public resetCart() {
    this.cartInfo.type = this.getDefaultBusinessModel();
    saveCartInStorage(this.cartInfo);
  }

  private calculateTotalPrice(): MoneyInput {
    const currency =
      this.items &&
      this.items[0] &&
      this.items[0].ratePlan &&
      this.items[0].ratePlan.totalPrice &&
      this.items[0].ratePlan.totalPrice.currencySymbol;
    const amount = this.items.reduce((total, item) => {
      if (!item || !item.ratePlan || !item.ratePlan || !item.ratePlan.totalPrice || !item.ratePlan.totalPrice.raw) {
        return total;
      }

      return total + item.ratePlan.totalPrice.raw;
    }, 0);

    return {
      amount,
      currency
    };
  }

  public getDefaultBusinessModel() {
    return this.isMixMatchEnabled ? CollectionBusinessModel.NA : CollectionBusinessModel.EC;
  }
}
